import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { STAGES, STAGE_LABEL, fmtINR, fmtDate, fmtDateTime } from "@/lib/format";
import { isBackwardMove } from "@/lib/stageRules";
import StageMoveConfirmDialog from "@/components/StageMoveConfirmDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, ChevronDown, Save, Trash2, Plus, MessageSquare, Mic, UserRound } from "lucide-react";
import DealVoiceJournalDialog from "@/components/DealVoiceJournalDialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import NewFollowupDialog from "@/components/NewFollowupDialog";
import WhatsAppButton from "@/components/WhatsAppButton";
import CallButton from "@/components/CallButton";
import MultiSelectProducts from "@/components/MultiSelectProducts";
import { MultiSelectServices, MultiSelectTrainings } from "@/components/MultiSelectCatalog";
import DealTimeline from "@/components/DealTimeline";
import useSmartBack from "@/hooks/useSmartBack";

export default function DealDetail() {
  const { id } = useParams();
  const goBack = useSmartBack("/deals");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activitySummary, setActivitySummary] = useState("");
  const [activityDetails, setActivityDetails] = useState("");
  const [openFu, setOpenFu] = useState(false);
  const [openVj, setOpenVj] = useState(false);
  // Pending stage move awaiting confirmation. The Select is intercepted so
  // we never optimistically mutate `form.stage` before the user confirms.
  const [pendingStage, setPendingStage] = useState(null);
  const [stageBusy, setStageBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deals/${id}`);
      setData(data);
      setForm({
        ...data.deal,
        next_followup_date: data.deal.next_followup_date ? data.deal.next_followup_date.slice(0, 10) : "",
      });
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // Strip blank decision-maker rows BEFORE submit. Pydantic would otherwise
      // accept-but-drop them now that `name` is optional, but the cleaner UX
      // is to throw them away client-side so users don't accidentally save an
      // empty stakeholder row.
      const cleanDMs = (form.decision_makers || []).filter(
        (d) => (d.name || d.phone || d.email || d.notes || "").toString().trim() !== ""
      );
      if (form.pipeline_type === "training" && form.training_start_date && form.training_end_date) {
        if (form.training_end_date < form.training_start_date) {
          toast.error("Training end date can't be before the start date");
          setSaving(false);
          return;
        }
      }
      const payload = {
        ...form,
        value: Number(form.value) || 0,
        next_followup_date: form.next_followup_date ? new Date(form.next_followup_date + "T12:00:00").toISOString() : null,
        client_id: form.client_id, // required by schema
        product_ids: form.product_ids || [],
        service_ids: form.service_ids || [],
        decision_makers: cleanDMs,
        negotiations: form.negotiations || [],
        purchase_enquiry: form.purchase_enquiry || null,
      };
      // ClientId & title required
      await api.patch(`/deals/${id}`, payload);
      toast.success("Saved");
      load();
    } catch (err) { toast.error(fmtError(err)); } finally { setSaving(false); }
  };

  // Intercept the stage <Select> change. Backward moves are blocked entirely
  // (mirrors backend), forward moves require an explicit confirm so reps
  // can't skim past the irreversible-stage warning.
  const onStageSelect = (next) => {
    if (!form || next === form.stage) return;
    if (isBackwardMove(form.stage, next)) {
      toast.error("Pipeline is one-way. Deals cannot move backward.");
      return;
    }
    setPendingStage(next);
  };

  const confirmStageMove = async () => {
    if (!pendingStage) return;
    setStageBusy(true);
    try {
      await api.post(`/deals/${id}/stage`, { stage: pendingStage });
      toast.success(`Moved to ${STAGE_LABEL[pendingStage]}`);
      setForm((f) => ({ ...f, stage: pendingStage }));
      setPendingStage(null);
      load();
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setStageBusy(false);
    }
  };

  const addActivity = async () => {
    if (!activitySummary.trim()) { toast.error("Summary required"); return; }
    try {
      await api.post("/activities", {
        type: "note",
        deal_id: id,
        client_id: data.deal.client_id,
        summary: activitySummary,
        details: activityDetails,
      });
      setActivitySummary(""); setActivityDetails("");
      toast.success("Activity logged");
      load();
    } catch (err) { toast.error(fmtError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this deal? This cannot be undone.")) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deleted");
      window.history.back();
    } catch (err) { toast.error(fmtError(err)); }
  };

  if (loading || !form) return <div className="p-6"><Skeleton className="h-12 w-1/2 mb-4" /><Skeleton className="h-96 rounded-3xl" /></div>;

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-[1100px] mx-auto">
      <button type="button" onClick={goBack} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground mb-4 tap" data-testid="back-deals">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <header className="flex flex-col md:flex-row md:flex-wrap md:items-start md:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{data.client?.company_name || "Deal"}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1 break-words">{form.title}</h1>
          <p className="font-display text-xl font-extrabold text-primary mt-2">{fmtINR(form.value)}</p>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2 w-full md:w-auto">
          <Select value={form.stage} onValueChange={onStageSelect}>
            <SelectTrigger className="h-11 rounded-2xl w-full md:w-48" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-2xl">{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <Button onClick={save} disabled={saving} className="rounded-2xl tap h-11 flex-1 md:flex-none" data-testid="deal-save"><Save className="h-4 w-4 mr-1" />{saving ? "Saving…" : "Save"}</Button>
            {data.client?.phone && (
              <>
                <CallButton
                  phone={data.client.phone}
                  size="default"
                  testId="call-deal-header"
                />
                <WhatsAppButton
                  phone={data.client.phone}
                  email={data.client.email}
                  contactName={data.client.contact_person || data.client.company_name}
                  dealTitle={form.title}
                  dealId={id}
                  clientId={data.client.id}
                  clientCompany={data.client.company_name}
                  action={form.next_action}
                  defaultTemplate="follow_up_deal"
                  size="default"
                  variant="outline"
                  label="Send Message"
                  testId="wa-deal-header"
                />
              </>
            )}
            <Button variant="outline" onClick={remove} className="rounded-2xl tap h-11 w-11 p-0 shrink-0 ml-auto md:ml-0" aria-label="Delete deal" data-testid="deal-delete"><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      {/* Main Contact Person — deal's own pick, else the account's PRIMARY.
          Changeable right here from the account's CRM contacts. */}
      <Card className="rounded-3xl border-primary-200/70 shadow-soft mb-5" data-testid="deal-main-contact">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-primary-50 text-primary-700 grid place-items-center shrink-0">
              <UserRound className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Main contact person</p>
              {data.primary_contact ? (
                <>
                  <p className="font-display font-extrabold truncate">
                    {data.primary_contact.name}
                    {data.primary_contact.designation ? <span className="text-xs font-semibold text-muted-foreground"> · {data.primary_contact.designation}</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[data.primary_contact.email, data.primary_contact.phone].filter(Boolean).join(" · ") || "No email / phone on file"}
                  </p>
                </>
              ) : (
                <p className="text-xs text-muted-foreground italic">No contact selected for this deal yet.</p>
              )}
            </div>
            <div className="shrink-0 w-full sm:w-56">
              <Select
                value={data.deal.main_contact_id || data.primary_contact?.id || "__none"}
                onValueChange={async (v) => {
                  try {
                    await api.post(`/deals/${id}/main-contact`, { contact_id: v === "__none" ? null : v });
                    toast.success("Main contact updated");
                    load();
                  } catch (e) { toast.error(fmtError(e)); }
                }}
              >
                <SelectTrigger className="rounded-2xl h-10" data-testid="deal-main-contact-select">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent className="rounded-2xl">
                  <SelectItem value="__none">— None —</SelectItem>
                  {(data.client_contacts || []).map((ct) => (
                    <SelectItem key={ct.id} value={ct.id}>
                      {ct.name}{ct.designation ? ` — ${ct.designation}` : ""}{ct.is_primary ? " ★" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(data.client_contacts || []).length === 0 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  No contacts on this account yet — <Link to={`/clients/${data.client?.id}`} className="underline font-semibold">add one</Link>.
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stage-wise price history — each stage keeps its recorded price;
          reductions show against the previous stage (corrections doc 3). */}
      {(data.deal.price_history || []).length > 1 && (
        <Card className="rounded-3xl border-border/60 shadow-soft mb-5" data-testid="deal-price-history">
          <CardContent className="p-4 sm:p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Stage-wise price history</p>
            <div className="space-y-1.5">
              {(data.deal.price_history || []).map((h, i, arr) => {
                const prev = i > 0 ? Number(arr[i - 1].value || 0) : null;
                const diff = prev !== null ? Number(h.value || 0) - prev : 0;
                return (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`price-history-row-${i}`}>
                    <span className="text-muted-foreground capitalize">{STAGE_LABEL[h.stage] || (h.stage || "").replace(/_/g, " ")}</span>
                    <span className="flex items-center gap-2 font-semibold">
                      {fmtINR(h.value)}
                      {prev !== null && diff !== 0 && (
                        <Badge variant="outline" className={`text-[10px] ${diff < 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {diff < 0 ? "↓" : "↑"} {fmtINR(Math.abs(diff))}
                        </Badge>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <Card className="rounded-3xl border-border/60 shadow-soft lg:col-span-2">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Mandatory</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-xs font-semibold">Title</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><Label className="text-xs font-semibold">Value (₹)</Label><Input type="number" className="rounded-2xl h-11 mt-1.5" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} /></div>
              <div><Label className="text-xs font-semibold">Next follow-up</Label><Input type="date" className="rounded-2xl h-11 mt-1.5" value={form.next_followup_date} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} data-testid="deal-next-fu" /></div>
              <div><Label className="text-xs font-semibold">Next action</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.next_action || ""} onChange={(e) => setForm({ ...form, next_action: e.target.value })} placeholder="Send proposal" /></div>
              <div className="sm:col-span-2">
                {/* Pipeline-aware picker — Service / Training deals show
                    the appropriate Services or Trainings catalog instead
                    of Products. Matches the New Deal + Convert Lead flow. */}
                {(form.pipeline_type || "product") === "product" ? (
                  <>
                    <Label className="text-xs font-semibold">Products</Label>
                    <MultiSelectProducts
                      value={form.product_ids || []}
                      onChange={(ids) => setForm({ ...form, product_ids: ids })}
                      testId="deal-detail-products-select"
                      placeholder="Select one or more products…"
                    />
                  </>
                ) : form.pipeline_type === "training" ? (
                  <>
                    <Label className="text-xs font-semibold">Trainings</Label>
                    <MultiSelectTrainings
                      value={form.service_ids || []}
                      onChange={(ids) => setForm({ ...form, service_ids: ids })}
                      testId="deal-detail-trainings-select"
                    />
                  </>
                ) : (
                  <>
                    <Label className="text-xs font-semibold">Technical services</Label>
                    <MultiSelectServices
                      value={form.service_ids || []}
                      onChange={(ids) => setForm({ ...form, service_ids: ids })}
                      testId="deal-detail-services-select"
                    />
                  </>
                )}
              </div>
              {/* Training-only date window — what calendar dates the
                  programme actually runs across. Appears on the Trainings
                  pipeline only; the field is meaningless on product /
                  service deals. */}
              {form.pipeline_type === "training" && (
                <>
                  <div>
                    <Label className="text-xs font-semibold">Training starts</Label>
                    <Input
                      type="date"
                      className="rounded-2xl h-11 mt-1.5"
                      value={form.training_start_date || ""}
                      onChange={(e) => setForm({ ...form, training_start_date: e.target.value })}
                      data-testid="deal-detail-training-start"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Training ends</Label>
                    <Input
                      type="date"
                      className="rounded-2xl h-11 mt-1.5"
                      value={form.training_end_date || ""}
                      min={form.training_start_date || undefined}
                      onChange={(e) => setForm({ ...form, training_end_date: e.target.value })}
                      data-testid="deal-detail-training-end"
                    />
                  </div>
                </>
              )}
            </div>

            <Collapsible open={openCollapse} onOpenChange={setOpenCollapse} className="mt-6">
              <CollapsibleTrigger className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground tap" data-testid="deal-optional-toggle">
                <ChevronDown className={`h-3 w-3 transition ${openCollapse ? "rotate-180" : ""}`} /> Optional context
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4 grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Label className="text-xs font-semibold">Client problem</Label><Textarea className="rounded-2xl mt-1.5" rows={2} value={form.client_problem || ""} onChange={(e) => setForm({ ...form, client_problem: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">Equipment / machine</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.equipment || ""} onChange={(e) => setForm({ ...form, equipment: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">Application area</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.application_area || ""} onChange={(e) => setForm({ ...form, application_area: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">ROI discussed</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.roi_discussed || ""} onChange={(e) => setForm({ ...form, roi_discussed: e.target.value })} /></div>
                <div><Label className="text-xs font-semibold">Competitor</Label><Input className="rounded-2xl h-11 mt-1.5" value={form.competitor || ""} onChange={(e) => setForm({ ...form, competitor: e.target.value })} /></div>
                <div className="sm:col-span-2"><Label className="text-xs font-semibold">Notes</Label><Textarea className="rounded-2xl mt-1.5" rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        <Card className="rounded-3xl border-border/60 shadow-soft">
          <CardContent className="p-6">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">Snapshot</p>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-muted-foreground">Client</p>
                {data.client ? (
                  <Link to={`/clients/${data.client.id}`} className="font-semibold text-sm hover:text-primary">{data.client.company_name}</Link>
                ) : <p className="font-semibold text-sm">—</p>}
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Owner</p>
                <p className="font-semibold text-sm">{data.owner?.name || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Stage</p>
                <Badge variant="outline" className="capitalize text-[10px] uppercase tracking-wider">{STAGE_LABEL[form.stage] || form.stage}</Badge>
              </div>
              {/* Highlighted Final Order Price tile — only when the deal is
                  Won. Shows the negotiated amount that lands on the invoice
                  and flows into Sales Pulse / leaderboard. */}
              {form.stage === "won" && (
                <div className="rounded-2xl p-3 bg-primary text-primary-foreground shadow-soft" data-testid="deal-final-order-price-tile">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Order placed</p>
                  <Input
                    type="number"
                    inputMode="decimal"
                    className="rounded-2xl h-12 mt-1.5 bg-white/10 text-primary-foreground placeholder:text-primary-foreground/60 text-2xl font-extrabold tabular-nums border-white/30"
                    value={form.final_order_price ?? form.value ?? ""}
                    onChange={(e) => setForm({ ...form, final_order_price: e.target.value === "" ? null : Number(e.target.value) })}
                    placeholder="₹ Final amount"
                    data-testid="deal-final-order-price-input"
                  />
                  <p className="text-[10px] opacity-80 mt-1">This is the figure on the invoice — flows into Sales Pulse.</p>
                </div>
              )}
              {form.last_interaction_summary && (
                <div>
                  <p className="text-xs text-muted-foreground">Last interaction</p>
                  <p className="text-sm italic">"{form.last_interaction_summary}"</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stage timeline — visualise how long the deal spent in each stage
          and total elapsed time. Pulls from the audit log, no extra writes. */}
      <DealTimeline dealId={id} pipelineType={form.pipeline_type || "product"} decisionMakers={form.decision_makers || []} />

      {/* Stakeholders, negotiations, purchase enquiry — collapsible bundles
          that B2B reps need but don't always populate. Each is a repeatable
          list with a "+" to add a row and an X to remove. */}
      <DealRepeatableSections form={form} setForm={setForm} />

      {/* Add activity */}
      <Card className="rounded-3xl border-border/60 shadow-soft mb-6">
        <CardContent className="p-6">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Log meeting / note</p>
          <Input className="rounded-2xl h-11" value={activitySummary} onChange={(e) => setActivitySummary(e.target.value)} placeholder="One-line summary…" data-testid="activity-summary-input" />
          <Textarea className="rounded-2xl mt-3" rows={2} value={activityDetails} onChange={(e) => setActivityDetails(e.target.value)} placeholder="Details (optional)" />
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button onClick={addActivity} className="rounded-2xl tap" data-testid="add-activity-button"><MessageSquare className="h-4 w-4 mr-1" /> Save log</Button>
            <Button variant="outline" onClick={() => setOpenVj(true)} className="rounded-2xl tap" data-testid="add-voice-journal-button"><Mic className="h-4 w-4 mr-1" /> Voice journal</Button>
            <Button variant="outline" onClick={() => setOpenFu(true)} className="rounded-2xl tap" data-testid="add-followup-button"><Plus className="h-4 w-4 mr-1" /> Follow-up</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-3xl border-border/60 shadow-soft">
        <CardContent className="p-4 space-y-2">
          <p className="px-2 pt-1 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Activity timeline</p>
          {(data.activities || []).length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No activities logged yet.</p>
          ) : data.activities.map((a) => (
            <div key={a.id} className="flex gap-3 p-3.5 rounded-2xl border border-border/60" data-testid={`deal-activity-${a.id}`}>
              <div className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 grid place-items-center shrink-0 text-[10px] font-bold uppercase">{(a.type || "n").slice(0, 2)}</div>
              <div className="flex-1">
                <p className="font-semibold text-sm">{a.summary}</p>
                {a.details && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{a.details}</p>}
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mt-2">
                  {a.created_by_name || "—"} · {fmtDateTime(a.created_at)}
                </p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <NewFollowupDialog open={openFu} onOpenChange={setOpenFu} defaultDealId={id} defaultClientId={data.deal.client_id} onCreated={load} />
      <DealVoiceJournalDialog open={openVj} onOpenChange={setOpenVj} deal={data.deal} onSaved={load} />

      <StageMoveConfirmDialog
        open={!!pendingStage}
        onOpenChange={(v) => { if (!v) setPendingStage(null); }}
        fromStage={form.stage}
        toStage={pendingStage}
        busy={stageBusy}
        onConfirm={confirmStageMove}
      />
    </div>
  );
}

const ROLE_OPTS = [
  { v: "economic_buyer", l: "Economic buyer" },
  { v: "technical_buyer", l: "Technical buyer" },
  { v: "user", l: "End user" },
  { v: "champion", l: "Champion" },
  { v: "gatekeeper", l: "Gatekeeper" },
];

// Standardised designation options surfaced as a dropdown on every
// stakeholder row. Keeps the data clean for reporting / search later.
// Order roughly maps from generic "User N" through escalating seniority.
const DESIGNATION_OPTS = [
  "User 1",
  "User 2",
  "User 3",
  "Department Head",
  "General Manager",
  "Plant Head",
  "Purchase Manager",
  "HOD Purchase",
  "HOD Engineering",
];

const NEGO_OUTCOME = [
  { v: "pending", l: "Pending" },
  { v: "counter", l: "Counter offered" },
  { v: "accepted", l: "Accepted" },
  { v: "rejected", l: "Rejected" },
];

function DealRepeatableSections({ form, setForm }) {
  const dms = form.decision_makers || [];
  const negos = form.negotiations || [];
  const pe = form.purchase_enquiry || {};

  const addDM = () => setForm({ ...form, decision_makers: [...dms, { name: "", designation: "", phone: "", email: "", role: "technical_buyer", notes: "" }] });
  const updateDM = (i, patch) => {
    const next = dms.map((d, idx) => idx === i ? { ...d, ...patch } : d);
    setForm({ ...form, decision_makers: next });
  };
  const removeDM = (i) => setForm({ ...form, decision_makers: dms.filter((_, idx) => idx !== i) });

  const addNego = () => setForm({
    ...form,
    negotiations: [...negos, { date: new Date().toISOString().slice(0, 10), amount: form.value || 0, outcome: "pending", note: "" }],
  });
  const updateNego = (i, patch) => {
    const next = negos.map((n, idx) => idx === i ? { ...n, ...patch } : n);
    setForm({ ...form, negotiations: next });
  };
  const removeNego = (i) => setForm({ ...form, negotiations: negos.filter((_, idx) => idx !== i) });

  const updatePE = (patch) => setForm({ ...form, purchase_enquiry: { ...pe, ...patch } });

  return (
    <Card className="rounded-3xl border-border/60 shadow-soft mb-6" data-testid="deal-stakeholders-card">
      <CardContent className="p-6 space-y-7">
        {/* Decision makers */}
        <section data-testid="deal-decision-makers">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Decision makers</p>
              <p className="text-xs text-muted-foreground mt-0.5">Everyone who influences this deal — log all stakeholders, not just the main contact.</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-2xl tap" onClick={addDM} data-testid="add-decision-maker">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          </div>
          {dms.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">No decision makers added yet.</p>
          ) : (
            <div className="space-y-3">
              {dms.map((d, i) => (
                <div key={d.id || i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-3 rounded-2xl border border-border/60" data-testid={`decision-maker-row-${i}`}>
                  <Input className="sm:col-span-3 rounded-2xl h-10" placeholder="Name" value={d.name || ""} onChange={(e) => updateDM(i, { name: e.target.value })} data-testid={`dm-name-${i}`} />
                  <Select value={d.designation || "__none"} onValueChange={(v) => updateDM(i, { designation: v === "__none" ? "" : v })}>
                    <SelectTrigger className="sm:col-span-3 rounded-2xl h-10" data-testid={`dm-designation-${i}`}><SelectValue placeholder="Designation" /></SelectTrigger>
                    <SelectContent className="rounded-2xl">
                      <SelectItem value="__none">— Select —</SelectItem>
                      {DESIGNATION_OPTS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="sm:col-span-2 rounded-2xl h-10" placeholder="Phone" value={d.phone || ""} onChange={(e) => updateDM(i, { phone: e.target.value })} data-testid={`dm-phone-${i}`} />
                  <Input className="sm:col-span-2 rounded-2xl h-10" placeholder="Email" value={d.email || ""} onChange={(e) => updateDM(i, { email: e.target.value })} data-testid={`dm-email-${i}`} />
                  <Select value={d.role || "technical_buyer"} onValueChange={(v) => updateDM(i, { role: v })}>
                    <SelectTrigger className="sm:col-span-1 rounded-2xl h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">{ROLE_OPTS.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" className="sm:col-span-1 rounded-2xl text-red-600 hover:bg-red-50" onClick={() => removeDM(i)} aria-label="Remove" data-testid={`remove-decision-maker-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Textarea
                    className="sm:col-span-12 rounded-2xl"
                    rows={1}
                    placeholder="Notes about this stakeholder (concerns, preferences, gatekeeper signals…)"
                    value={d.notes || ""}
                    onChange={(e) => updateDM(i, { notes: e.target.value })}
                    data-testid={`dm-notes-${i}`}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Negotiations */}
        <section data-testid="deal-negotiations">
          <div className="flex items-end justify-between mb-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Negotiation rounds</p>
              <p className="text-xs text-muted-foreground mt-0.5">Log every counter and final number — handovers stay clean and renegotiations have full context.</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-2xl tap" onClick={addNego} data-testid="add-negotiation">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add round
            </Button>
          </div>
          {negos.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">No negotiation rounds logged yet.</p>
          ) : (
            <div className="space-y-3">
              {negos.map((n, i) => (
                <div key={n.id || i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-3 rounded-2xl border border-border/60" data-testid={`negotiation-row-${i}`}>
                  <Input type="date" className="sm:col-span-3 rounded-2xl h-10" value={(n.date || "").slice(0, 10)} onChange={(e) => updateNego(i, { date: e.target.value })} />
                  <Input type="number" className="sm:col-span-3 rounded-2xl h-10" placeholder="Amount (₹)" value={n.amount ?? ""} onChange={(e) => updateNego(i, { amount: Number(e.target.value) || 0 })} />
                  <Select value={n.outcome || "pending"} onValueChange={(v) => updateNego(i, { outcome: v })}>
                    <SelectTrigger className="sm:col-span-2 rounded-2xl h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-2xl">{NEGO_OUTCOME.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="sm:col-span-3 rounded-2xl h-10" placeholder="Note" value={n.note || ""} onChange={(e) => updateNego(i, { note: e.target.value })} />
                  <Button variant="ghost" size="sm" className="sm:col-span-1 rounded-2xl text-red-600 hover:bg-red-50" onClick={() => removeNego(i)} aria-label="Remove" data-testid={`remove-negotiation-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Purchase enquiry — single record per deal (procurement contact). */}
        <section data-testid="deal-purchase-enquiry">
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Purchase enquiry</p>
            <p className="text-xs text-muted-foreground mt-0.5">Procurement contact details — usually different from the technical evaluator.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold">Purchaser name</Label><Input className="rounded-2xl h-11 mt-1.5" value={pe.purchaser_name || ""} onChange={(e) => updatePE({ purchaser_name: e.target.value })} data-testid="pe-name" /></div>
            <div><Label className="text-xs font-semibold">Designation</Label><Input className="rounded-2xl h-11 mt-1.5" value={pe.purchaser_designation || ""} onChange={(e) => updatePE({ purchaser_designation: e.target.value })} data-testid="pe-designation" /></div>
            <div><Label className="text-xs font-semibold">Email</Label><Input type="email" className="rounded-2xl h-11 mt-1.5" value={pe.purchaser_email || ""} onChange={(e) => updatePE({ purchaser_email: e.target.value })} data-testid="pe-email" /></div>
            <div><Label className="text-xs font-semibold">Phone</Label><Input className="rounded-2xl h-11 mt-1.5" value={pe.purchaser_phone || ""} onChange={(e) => updatePE({ purchaser_phone: e.target.value })} data-testid="pe-phone" /></div>
            <div><Label className="text-xs font-semibold">Enquiry ref / PO no.</Label><Input className="rounded-2xl h-11 mt-1.5" value={pe.enquiry_ref || ""} onChange={(e) => updatePE({ enquiry_ref: e.target.value })} data-testid="pe-ref" /></div>
            <div><Label className="text-xs font-semibold">Enquiry date</Label><Input type="date" className="rounded-2xl h-11 mt-1.5" value={(pe.enquiry_date || "").slice(0, 10)} onChange={(e) => updatePE({ enquiry_date: e.target.value })} data-testid="pe-date" /></div>
            <div className="sm:col-span-2"><Label className="text-xs font-semibold">Notes</Label><Textarea className="rounded-2xl mt-1.5" rows={2} value={pe.notes || ""} onChange={(e) => updatePE({ notes: e.target.value })} data-testid="pe-notes" /></div>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
